// Operasyon panosu — canlı salon doluluğu (harita) + metrikler. Doluluk hem
// onaya göre (paid) hem toplam (paid + ön rezervasyon) gösterilir.

import { useSeatmap } from '@/features/booking/api/useSeatmap.ts';
import { useSeatmapState } from '@/features/booking/api/useSeatmapState.ts';
import { cn } from '@/lib/cn.ts';
import { MVP_EVENT_ID, RESERVATION_SOURCE_LABELS_TR } from '@okn/shared';
import { BadgeCheck, Clock, Mail, RefreshCw, Ticket, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from './auth.ts';
import { fmtDateTime } from './lib/format.ts';
import type { Metrics } from './lib/types.ts';
import { StatusBadge } from './ui/Badge.tsx';
import { Card, CardHeader } from './ui/Card.tsx';
import { OccupancyGauge } from './ui/OccupancyGauge.tsx';
import { PageHeader } from './ui/PageHeader.tsx';
import { Skeleton } from './ui/Skeleton.tsx';
import { StatTile } from './ui/StatTile.tsx';
import { OccupancyLegend, VenueOccupancyMap } from './ui/VenueOccupancyMap.tsx';

const STATE_TOKEN = 'admin-dashboard-readonly-token';

const STATUS_BARS = [
  { key: 'paid', label: 'Onaylandı', color: 'var(--success)' },
  { key: 'reserved', label: 'Ön Rezervasyon', color: 'var(--gold)' },
  { key: 'cancelled', label: 'İptal', color: 'var(--accent)' },
  { key: 'noShow', label: 'Gelmedi', color: 'var(--text-faint)' },
] as const;

export function Dashboard() {
  const [m, setM] = useState<Metrics | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const map = useSeatmap(MVP_EVENT_ID);
  const { state, refresh: refreshState } = useSeatmapState(MVP_EVENT_ID, STATE_TOKEN);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await adminApi<Metrics>(`/admin/metrics?eventId=${MVP_EVENT_ID}`);
      setM(data);
      refreshState();
    } catch {
      // surfaced as empty state below
    } finally {
      setRefreshing(false);
    }
  }, [refreshState]);

  useEffect(() => {
    load();
  }, [load]);

  const sets = useMemo(
    () => ({
      paidIds: new Set(state?.soldSeatIds ?? []),
      pendingIds: new Set(state?.reservedSeatIds ?? []),
      heldIds: new Set(state?.heldSeatIds ?? []),
    }),
    [state],
  );

  const sellable = m?.seats.sellable ?? 0;
  const confirmedPct = sellable > 0 ? Math.round(((m?.confirmedSeats ?? 0) / sellable) * 100) : 0;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        kicker="Operasyon Panosu"
        title="Dashboard"
        description="Salonun anlık doluluğu, bekleyenler ve hareketler."
        actions={
          <button
            type="button"
            onClick={load}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 text-xs text-text-muted transition-colors hover:border-border-strong hover:text-text"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
            Yenile
          </button>
        }
      />

      {!m ? (
        <div className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <>
          <section className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <StatTile
              label="Onay Doluluğu"
              value={`${confirmedPct}%`}
              sub={`${m.confirmedSeats} / ${sellable} koltuk`}
              icon={<BadgeCheck className="h-4 w-4" />}
              accent="crimson"
            />
            <StatTile
              label="Bekleyen"
              value={m.reservations.reserved}
              sub={`${m.pendingSeats} koltuk · ödeme bekliyor`}
              icon={<Clock className="h-4 w-4" />}
              accent="gold"
            />
            <StatTile
              label="Onaylanmış"
              value={m.reservations.paid}
              sub="rezervasyon"
              icon={<Ticket className="h-4 w-4" />}
              accent="success"
            />
            <StatTile
              label="E-posta Gönderildi"
              value={m.emailsSent}
              sub="davetiye / bilgilendirme"
              icon={<Mail className="h-4 w-4" />}
            />
            <StatTile
              label="Canlı Hold"
              value={m.seatsHeld}
              sub="aktif checkout"
              icon={<Users className="h-4 w-4" />}
            />
          </section>

          <section className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[1.55fr_1fr]">
            {/* Live hall map */}
            <Card className="flex flex-col">
              <CardHeader
                title="Salon Doluluğu"
                action={
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-text-faint">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success/60 motion-reduce:hidden" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
                    </span>
                    canlı
                  </span>
                }
              />
              <div className="flex-1 p-4">
                {map.status === 'ready' ? (
                  <>
                    <VenueOccupancyMap allSeats={map.data.allSeats} {...sets} />
                    <div className="mt-4 border-t border-border pt-3">
                      <OccupancyLegend />
                    </div>
                  </>
                ) : (
                  <Skeleton className="h-80 w-full" />
                )}
              </div>
            </Card>

            {/* Gauge + status/source */}
            <div className="space-y-4">
              <Card className="flex flex-col">
                <CardHeader title="Onay Doluluğu" />
                <div className="flex flex-1 flex-col items-center justify-center py-5">
                  <OccupancyGauge pct={confirmedPct} filled={m.confirmedSeats} total={sellable} />
                  <p className="mt-3 text-xs text-text-faint">
                    Toplam doluluk (ön rez. dahil)
                    <span className="mono ml-1.5 text-text-muted tnum">%{m.occupancyPct}</span>
                  </p>
                </div>
              </Card>

              <Card>
                <CardHeader title="Durum Dağılımı" />
                <div className="space-y-3 px-5 py-5">
                  {STATUS_BARS.map((s) => {
                    const value = m.reservations[s.key as keyof typeof m.reservations];
                    const pct = m.reservations.total > 0 ? (value / m.reservations.total) * 100 : 0;
                    return (
                      <div key={s.key}>
                        <div className="flex items-baseline justify-between text-sm">
                          <span className="text-text-muted">{s.label}</span>
                          <span className="mono text-text tnum">{value}</span>
                        </div>
                        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface">
                          <div
                            className="h-full rounded-full transition-[width] duration-500"
                            style={{ width: `${pct}%`, backgroundColor: s.color }}
                          />
                        </div>
                      </div>
                    );
                  })}

                  <div className="!mt-5 border-t border-border pt-4">
                    <p className="kicker mb-2.5">Kaynak</p>
                    {Object.entries(m.bySource).length === 0 ? (
                      <p className="text-sm text-text-faint">Henüz veri yok</p>
                    ) : (
                      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                        {Object.entries(m.bySource).map(([source, count]) => (
                          <div key={source} className="flex items-center gap-1.5 text-sm">
                            <span className="text-text-muted">
                              {RESERVATION_SOURCE_LABELS_TR[
                                source as keyof typeof RESERVATION_SOURCE_LABELS_TR
                              ] ?? source}
                            </span>
                            <span className="mono text-text tnum">{count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </section>

          <section className="mt-6">
            <Card>
              <CardHeader
                title="Son Rezervasyonlar"
                action={
                  <Link
                    to="/admin/rezervasyonlar"
                    className="text-xs text-text-muted transition-colors hover:text-text"
                  >
                    Tümü →
                  </Link>
                }
              />
              <div className="overflow-x-auto">
                <table className="w-full min-w-[480px] text-sm">
                  <tbody className="divide-y divide-border">
                    {m.recent.map((r) => (
                      <tr key={r.id} className="transition-colors hover:bg-surface">
                        <td className="px-4 py-2.5">
                          <Link
                            to={`/admin/rezervasyonlar/${r.id}`}
                            className="mono text-accent tnum hover:underline"
                          >
                            {r.referenceCode}
                          </Link>
                        </td>
                        <td className="px-4 py-2.5 text-text">{r.fullName}</td>
                        <td className="px-4 py-2.5 text-right text-text-muted tnum">
                          {r.seatCount} koltuk
                        </td>
                        <td className="px-4 py-2.5">
                          <StatusBadge status={r.status} />
                        </td>
                        <td className="px-4 py-2.5 text-right text-[11px] text-text-faint tnum">
                          {fmtDateTime(r.createdAt)}
                        </td>
                      </tr>
                    ))}
                    {m.recent.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-12 text-center text-sm text-text-faint">
                          Henüz rezervasyon yok
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </section>
        </>
      )}
    </div>
  );
}
